/* eslint-disable max-len */
/* eslint-disable react/prop-types */

import {
  CalendarMonth,
  CalendarViewMonth,
  FilterAltOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { setGlobalModals } from '../../redux/app/actions';
import { SCHEDULE_MODAL_TYPE } from '../../redux/app/types';
import { getBarnBoard } from '../../redux/board/actions';
import {
  getAllHorseLocations,
  setSelectedLocation,
} from '../../redux/location/actions';
import {
  initializePlannerUi,
  setPlannerCalendarView,
  setPlannerCurrentView,
  setPlannerEndDate,
  setPlannerGridView,
  setPlannerStartDate,
  setPlannerWeekStyle,
} from '../../redux/planner/actions';
import { grey } from '../../utils/brandColors';
import { getTimeZoneOffset } from '../../utils/dateHelper';
import LocationAutocomplete from '../Autocompletes/Locations';
import Calendar from './components/Calendar';
import FilterBar from './components/FilterBar';
import PlannerAppBarComponent from './components/PlannerAppBar';
import TaskCalendarOverview from './components/TaskCalendarOverview';
import TaskCalendarEventMenu from './components/TaskCalendarOverview/components/TaskCalendarEventMenu';

function getTaskTypeTags(calendarEvents, horseTasks, barnTasks) {
  // add task type condition if present
  let taskTypeCondition = []
  if (calendarEvents) {
    taskTypeCondition = [...taskTypeCondition, calendarEvents];
  }
  if (horseTasks) {
    taskTypeCondition = [...taskTypeCondition, horseTasks];
  }
  if (barnTasks) {
    taskTypeCondition = [...taskTypeCondition, barnTasks];
  }

  return taskTypeCondition;
}

const getBarnUserFilterValue = (barnUser) => (barnUser
  ? (barnUser.auth0UserId || barnUser.email || barnUser.userFullName)
  : null);

const PlannerComponent = ({
  calendarEvents,
  horseTasks,
  barnTasks,
  defaultView,
  defaultCalendarView,
  defaultAssignment,
  onPerformedAction: onPerformedActionProp,
  navigate,
  defaultBarnHorses,
  condensed,
}) => {
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();
  const currentView = useSelector((state) => state.planner.currentView);
  const weekStyle = useSelector((state) => state.planner.weekStyle || 'isoWeek');
  const gridView = useSelector((state) => state.planner.gridView || 'week');
  const calendarView = useSelector((state) => state.planner.calendarView || 'week');
  const plannerStartDate = useSelector((state) => state.planner.startDate);
  const plannerEndDate = useSelector((state) => state.planner.endDate);

  // React State
  const startDate = React.useMemo(() => {
    if (plannerStartDate) {
      return new Date(plannerStartDate);
    }
    return (defaultView === 'calendar' && defaultCalendarView === 'month')
      ? moment().startOf('month').toDate()
      : moment().startOf(weekStyle).startOf('day').toDate();
  }, [defaultCalendarView, defaultView, plannerStartDate, weekStyle]);
  const endDate = React.useMemo(() => {
    if (plannerEndDate) {
      return new Date(plannerEndDate);
    }
    return (defaultView === 'calendar' && defaultCalendarView === 'month')
      ? moment().endOf('month').toDate()
      : moment().endOf(weekStyle).endOf('day').toDate();
  }, [defaultCalendarView, defaultView, plannerEndDate, weekStyle]);

  const [selectedSlot, setSelectedSlot] = React.useState(null);
  const [openQuickSelectMenu, setOpenQuickSelectMenu] = React.useState(false);
  const [selectedBarnHorses, setSelectedBarnHorses] = React.useState(defaultBarnHorses || []);
  const [slotBarnHorses, setSlotBarnHorses] = React.useState([])
  const [tags, setTags] = React.useState([]);
  const [includeCalendarEvents, setIncludeCalendarEvents] = React.useState(calendarEvents);
  const [includeHorseTasks, setIncludeHorseTasks] = React.useState(horseTasks);
  const [includeBarnTasks, setIncludeBarnTasks] = React.useState(barnTasks);
  const [selectedScheduleUser, setSelectedScheduleUser] = React.useState(null);
  const [selectedParticipantUser, setSelectedParticipantUser] = React.useState(null);
  const [eventTemplatesFilter, setEventTemplatesFilter] = React.useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = React.useState(null);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = React.useState(false);

  const allBarnHorses = useSelector((state) => state.barn.allBarnHorses);
  const isFetchingBoard = useSelector((state) => state.board.isFetchingBoard);

  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const eventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const selectedLocation = useSelector((state) => state.location.selectedLocation);
  const planningDay = useSelector((state) => state.board.planningDay);
  const locations = useSelector((state) => state.location.locations);

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = condensed || useMediaQuery(theme.breakpoints.down('lg'));
  const plannerCurrentView = currentView || defaultView;
  const setStartDate = React.useCallback((newStartDate) => {
    dispatch(setPlannerStartDate(newStartDate));
  }, [dispatch]);
  const setEndDate = React.useCallback((newEndDate) => {
    dispatch(setPlannerEndDate(newEndDate));
  }, [dispatch]);

  const sortedIds = (values) => [...values].map(Number).sort((left, right) => left - right);
  const plannerScopeKey = JSON.stringify([
    selectedBarn?.id,
    selectedBarn?.tenant,
    selectedLocation?.id,
    selectedLocation?.timezone,
    startDate,
    endDate,
    plannerCurrentView,
    sortedIds(selectedBarnHorses.map((horse) => horse.pingumTagId)),
    sortedIds(tags.map((tag) => tag.id)),
    sortedIds(eventTemplatesFilter || []),
    includeCalendarEvents,
    includeHorseTasks,
    includeBarnTasks,
    getBarnUserFilterValue(selectedScheduleUser),
    getBarnUserFilterValue(selectedParticipantUser),
  ]);
  const currentScope = React.useRef(null);
  if (currentScope.current?.key !== plannerScopeKey) currentScope.current = { key: plannerScopeKey };
  const scope = currentScope.current;
  const mounted = React.useRef(false);
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const isCurrentScope = () => mounted.current && currentScope.current === scope;

  const loadData = (forceBarnHorseRefresh = false, { background = true } = {}) => {
    if (!isCurrentScope() || !selectedBarn?.id || !selectedLocation?.id) return;
    if (selectedLocation.barn?.id && String(selectedLocation.barn.id) !== String(selectedBarn.id)) return;

    // ONly load if tempaltes have loaded
    if (eventTemplates && eventTemplates.length > 0) {
      // Because we cache barn horses, might might need to override that if horses were added on this page
      dispatch(getBarnBoard(
        startDate,
        endDate,
        selectedLocation,
        selectedBarnHorses,
        getTaskTypeTags(includeCalendarEvents && selectedBarn.barnMetadata.eventTaskTypeTagId, includeHorseTasks && selectedBarn.barnMetadata.horseTaskTypeTagId, includeBarnTasks && selectedBarn.barnMetadata.barnTaskTypeTagId),
        tags.map((t) => t.id),
        {
          forceBarnHorseRefresh: forceBarnHorseRefresh === true,
          background,
          // eslint-disable-next-line no-nested-ternary
          auth0UserId: getBarnUserFilterValue(selectedScheduleUser),
          scheduleAssignmentAuth0UserId: getBarnUserFilterValue(selectedParticipantUser),
          eventTemplateIds: (eventTemplatesFilter && eventTemplatesFilter.length > 0 && eventTemplatesFilter) || null,
        },
      ));
    }
  }

  React.useEffect(() => {
    if (defaultAssignment && defaultAssignment.id) {
      // set the default assignment/schedule user if passed in
      setSelectedScheduleUser(defaultAssignment)
    }
  }, [defaultAssignment]);

  React.useEffect(() => {
    if (defaultBarnHorses && defaultBarnHorses.length > 0) {
      setSelectedBarnHorses(defaultBarnHorses);
    }
  }, [defaultBarnHorses]);

  React.useEffect(() => {
    dispatch(initializePlannerUi({
      defaultView,
      defaultCalendarView,
    }));
  }, [defaultCalendarView, defaultView, dispatch]);

  React.useEffect(() => {
    if (!isSmallScreen) {
      setIsFilterDrawerOpen(false);
    }
  }, [isSmallScreen]);

  React.useEffect(() => {
    moment.updateLocale('en', {
      week: {
        dow: weekStyle === 'isoWeek'
          ? 1 // First day of week is Monday
          : 0,
        doy: 4, // First week of year must contain 4 January (7 + 1 - 4)
      },
    });
    if (plannerCurrentView !== 'calendar' && gridView === 'week') {
      const referenceStartMoment = startDate
        ? moment(startDate)
        : moment();
      const referenceEndMoment = endDate
        ? moment(endDate)
        : referenceStartMoment.clone();
      const nextStartDate = referenceStartMoment.clone().startOf(weekStyle).startOf('day').toDate();
      const nextEndDate = referenceEndMoment.clone().endOf(weekStyle).endOf('day').toDate();
      if (!startDate || nextStartDate.getTime() !== startDate.getTime()) {
        setStartDate(nextStartDate);
      }
      if (!endDate || nextEndDate.getTime() !== endDate.getTime()) {
        setEndDate(nextEndDate);
      }
    }
  }, [endDate, gridView, plannerCurrentView, setEndDate, setStartDate, startDate, weekStyle])

  React.useEffect(() => {
    if (selectedLocation && selectedLocation.id) {
      loadData()
    }
  }, [plannerScopeKey, eventTemplates, allBarnHorses])

  React.useEffect(() => {
    dispatch(getAllHorseLocations(startDate, endDate));
  }, [startDate])

  React.useEffect(() => {
    if (!selectedLocation.id && window.localStorage.getItem('barn_board_location') !== null
      && locations.content.find((l) => l.id === JSON.parse(window.localStorage.getItem('barn_board_location')).id)) {
      const newLocation = locations.content.find((l) => l.id === JSON.parse(window.localStorage.getItem('barn_board_location')).id);
      if (newLocation) {
        dispatch(setSelectedLocation(newLocation));
      }
    } else if (!selectedLocation.id && locations.length === 0) {
      dispatch(setSelectedLocation(locations[0]));
    }
  }, [locations]);

  const scheduleClick = (calendarEvent = false, isBarnTask = false, isHorseTask = false) => {
    const today = moment().startOf('day').toDate();
    setSelectedSlot({
      start: today,
      end: new Date(today.getTime() + 86400000), // mimicks an all day event
      barnHorses: slotBarnHorses,
      calendarEvent,
      isBarnTask,
      isHorseTask,
    });
    setOpenQuickSelectMenu(true)
  }

  const handleEventClick = ({
    resource: schedule,
    ...event
  }) => {
    dispatch(setGlobalModals(SCHEDULE_MODAL_TYPE, null, schedule, event, {
      locationId: event.eventTask
        ? selectedLocation.id
        : null, // if set, this is a calendar event
      taskTagIds: [],
      eventTemplateTagTypeFilter: selectedBarn.barnMetadata.eventTaskTypeTagId,
      openInSeries: false,
      startDate: null,
    }, (reload) => {
      if (reload === true && isCurrentScope()) loadData(false, { background: true });
    }));
  };

  const handlePlannedAction = (reload, callback) => {
    if (!isCurrentScope()) return;
    if (reload === true) loadData(false, { background: true });
    if (callback) {
      callback();
    }
    setSelectedSlot(null);
    onPerformedActionProp(); // Used by onboarding to observe the completed planner interaction
  }

  const handleOpenScheduleModal = (eventTemplate, isCalendarEvent, replacementSlot) => {
    let taskTagIds = [];

    const slot = replacementSlot || selectedSlot; // Use passed in slot, otherwise use state slot

    if (eventTemplate.tags.find((t) => t.id === selectedBarn.barnMetadata.barnTaskTypeTagId)) {
      // this is a barn task so add a location
      taskTagIds = [{
        id: parseInt(selectedLocation.pingumTagId, 10),
        tagType: { id: selectedBarn.barnMetadata.locationTagTypeId },
      }]
    } else if (eventTemplate.tags.find((t) => t.id === selectedBarn.barnMetadata.horseTaskTypeTagId || t.id === selectedBarn.barnMetadata.eventTaskTypeTagId)) {
      // horse task so add any selected horses
      taskTagIds = ((slot && slot.barnHorses) || selectedBarnHorses || []).map((bh) => ({
        id: bh.pingumTagId
          ? parseInt(bh.pingumTagId, 10)
          : bh,
        tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
      }));
    }

    const slotDuration = slot && (moment(slot.end).valueOf() - moment(slot.start).valueOf() >= 86400000
      ? null
      : moment(slot.end).valueOf() - moment(slot.start).valueOf());

    dispatch(setGlobalModals(SCHEDULE_MODAL_TYPE, null, null, null, {
      locationId: isCalendarEvent && selectedLocation && selectedLocation.id,
      taskTagIds,
      eventTemplateTagTypeFilter: (eventTemplate && eventTemplate.id)
        ? eventTemplate.id
        : null,
      openInSeries: (slot && slot.openInSeries) || false,
      startDate: slot
        ? moment(slot.start).toDate()
        : moment(startDate).set('hours', moment().get('hour')).startOf('hour').add(1, 'hours')
          .toDate(),
      duration: (slotDuration && slotDuration < 1800000 && slotDuration > 10000)
        ? 1800000
        : slotDuration, // don't allow less than 15 mintues by default
      assignment: selectedScheduleUser,
      assignmentTaskMap: slot && slot.barnHorsesAssignmentMapping, // Used for suggestions or TaskCalendarEventMenu Advanced
      workflows: slot && slot.workflows,
      drawer: slot?.drawer || false,
      restOfSuggestions: slot?.restOfSuggestions || [],
    }, (reload) => {
      handlePlannedAction(reload, slot && slot.callback);
    }));
  }

  const handleChangeLocation = (newLocation) => {
    if (newLocation && newLocation.id) {
      if (newLocation.timezone !== (selectedLocation && selectedLocation.timezone)) {
        // reset dates for timezone
        const timezoneOffsetMillis = getTimeZoneOffset(newLocation.timezone);
        setStartDate(new Date(startDate.getTime() + timezoneOffsetMillis))
        setEndDate(new Date(endDate.getTime() + timezoneOffsetMillis))
      }

      global.window.localStorage.setItem('barn_board_location', JSON.stringify(newLocation));
      global.window.localStorage.setItem('barn_board_location_location_board', null);
      dispatch(setSelectedLocation(newLocation));
    }
  };

  const filterBarProps = {
    selectedScheduleUser,
    setSelectedScheduleUser,
    selectedParticipantUser,
    setSelectedParticipantUser,
    scheduleClick,
    setIncludeCalendarEvents,
    setIncludeHorseTasks,
    setIncludeBarnTasks,
    includeCalendarEvents,
    includeHorseTasks,
    includeBarnTasks,
    setSelectedBarnHorses,
    defaultBarnHorses,
    selectedBarnHorses,
    setTags,
    tags,
    setCurrentView: (nextView) => dispatch(setPlannerCurrentView(nextView)),
    currentView: plannerCurrentView,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    weekStyle,
    setWeekStyle: (nextWeekStyle) => dispatch(setPlannerWeekStyle(nextWeekStyle)),
    eventTemplatesFilter,
    setEventTemplatesFilter,
    selectedCalendarId,
    setSelectedCalendarId,
  };

  const selectedBarnHorseTagIds = React.useMemo(
    () => (selectedBarnHorses || [])
      .map((barnHorse) => {
        if (barnHorse && barnHorse.pingumTagId) {
          return parseInt(barnHorse.pingumTagId, 10);
        }
        return null;
      })
      .filter((tagId) => !!tagId),
    [selectedBarnHorses],
  );
  const selectedSlotForMenu = React.useMemo(() => {
    if (!selectedSlot) {
      return {
        assignment: selectedScheduleUser,
        barnHorses: selectedBarnHorseTagIds,
      };
    }
    const menuSlotBarnHorses = (selectedSlot.barnHorses && selectedSlot.barnHorses.length > 0)
      ? selectedSlot.barnHorses
      : selectedBarnHorseTagIds;
    return {
      ...selectedSlot,
      assignment: selectedScheduleUser,
      barnHorses: menuSlotBarnHorses,
    };
  }, [selectedBarnHorseTagIds, selectedScheduleUser, selectedSlot]);

  return (
    <Box>
      {
        !(!selectedLocation || !selectedLocation.id) && !planningDay && (
          <PlannerAppBarComponent
            selectedLocation={selectedLocation}
            navigate={navigate}
            scheduleClick={scheduleClick}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            startDate={startDate}
            endDate={endDate}
            handleRefresh={loadData}
            isFetchingBoard={isFetchingBoard}
            weekStyle={weekStyle}
            setWeekStyle={(nextWeekStyle) => dispatch(setPlannerWeekStyle(nextWeekStyle))}
            filterComponent={isSmallScreen && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Tooltip title="Toggle Calendar or Planning View">
                  <IconButton
                    onClick={() => {
                      const switchingToCalendar = plannerCurrentView === 'grid';
                      const referenceMoment = startDate
                        ? moment(startDate)
                        : moment();
                      if (switchingToCalendar) {
                        const calendarReferenceMoment = referenceMoment.clone();
                        if (gridView === 'day') {
                          dispatch(setPlannerCalendarView('day'));
                          setStartDate(calendarReferenceMoment.clone().startOf('day').toDate());
                          setEndDate(calendarReferenceMoment.clone().endOf('day').toDate());
                        } else {
                          dispatch(setPlannerCalendarView('week'));
                          setStartDate(calendarReferenceMoment.clone().startOf('week').startOf('day').toDate());
                          setEndDate(calendarReferenceMoment.clone().endOf('week').endOf('day').toDate());
                        }
                      } else {
                        const gridReferenceMoment = referenceMoment.clone();
                        if (calendarView === 'day' || calendarView === 'agenda') {
                          dispatch(setPlannerGridView('day'));
                          setStartDate(gridReferenceMoment.clone().startOf('day').toDate());
                          setEndDate(gridReferenceMoment.clone().endOf('day').toDate());
                        } else {
                          dispatch(setPlannerGridView('week'));
                          setStartDate(gridReferenceMoment.clone().startOf(weekStyle).startOf('day').toDate());
                          setEndDate(gridReferenceMoment.clone().endOf(weekStyle).endOf('day').toDate());
                        }
                      }
                      dispatch(setPlannerCurrentView(switchingToCalendar
                        ? 'calendar'
                        : 'grid'));
                    }}
                  >{plannerCurrentView === 'calendar'
                    ? <CalendarMonth />
                    : <CalendarViewMonth />}
                  </IconButton>
                </Tooltip>
                {(
                  mobile
                    ? (
                      <IconButton onClick={() => setIsFilterDrawerOpen(true)}><FilterAltOutlined /></IconButton>
                    )
                    : (
                      <Button
                        variant="outlined"
                        startIcon={<FilterAltOutlined />}
                        onClick={() => setIsFilterDrawerOpen(true)}
                        fullWidth
                      >
                        Filters
                      </Button>
                    )
                )}
              </Box>
            )}
          />
        )
      }
      <Grid container direction="row">
        {
          (!selectedLocation || !selectedLocation.id) && (
            <Grid
              size={{ xs: 12 }}
            >
              <Box
                sx={{
                  margin: 'auto',
                  p: 2,
                  textAlign: 'center',
                }}
              >
                <InfoOutlined
                  sx={{
                    fontSize: '3.5rem',
                    color: grey[400],
                    m: 2,
                  }}
                />
                <Typography align="center" component="div" sx={{ mb: 4 }}>
                  Select a location
                </Typography>
                <Box
                  sx={{
                    margin: 'auto',
                    mb: 2,
                    maxWidth: 300,
                  }}
                >
                  <LocationAutocomplete
                    onSelect={(e, newLocations) => {
                      handleChangeLocation(newLocations)
                    }}
                    value={selectedLocation}
                    multiple={false}
                    size="small"
                    label="Select Location"
                    v2
                  />
                </Box>
              </Box>
            </Grid>
          )
        }
        {!isSmallScreen && (
          <Grid size="auto">
            {
              !(!selectedLocation || !selectedLocation.id) && (
                <Box
                  sx={{
                    pl: 2,
                    display: planningDay
                      ? 'none'
                      : 'inherit',
                  }}
                >
                  <FilterBar {...filterBarProps} />
                </Box>
              )
            }
          </Grid>
        )}
        <Grid size="grow">
          {
            (selectedLocation && selectedLocation.id && plannerCurrentView === 'calendar')
            && (
              <Calendar
                handleEventClick={handleEventClick}
                setSelectedSlot={setSelectedSlot}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                setOpenQuickSelectMenu={setOpenQuickSelectMenu}
                defaultCalendarView={defaultCalendarView}
                startDate={startDate}
                endDate={endDate}
              />
            )
          }

          {
            (selectedLocation && selectedLocation.id && plannerCurrentView === 'grid') && (
              <TaskCalendarOverview
                navigate={navigate}
                plannerScopeKey={plannerScopeKey}
                handleRefresh={loadData}
                handleOpenScheduleModal={handleOpenScheduleModal}
                barnTasks={barnTasks}
                horseTasks={includeHorseTasks}
                eventTemplateIds={eventTemplatesFilter}
                filterTagIds={tags.map((tag) => tag.id)}
                selectedParticipantUser={selectedParticipantUser}
                selectedScheduleUser={selectedScheduleUser}
                setOpenQuickSelectMenu={setOpenQuickSelectMenu}
                setSelectedSlot={setSelectedSlot}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                startDate={startDate}
                endDate={endDate}
                setSlotBarnHorses={setSlotBarnHorses}
                loadData={loadData}
              />
            )
          }
          {
            allBarnHorses.length <= 0 && !isFetchingBoard && (
              <Button size="small" variant="contained" onClick={() => navigate('/horses')}>Create and manage horses</Button>
            )
          }
          <TaskCalendarEventMenu
            tagsFilter={tags || []}
            includeBarnTasks={includeBarnTasks && (!selectedSlot || !selectedSlot.isHorseTask)}
            includeHorseTasks={includeHorseTasks && (!selectedSlot || !selectedSlot.isBarnTask)}
            open={openQuickSelectMenu}
            onClose={(plannedAction, reload, callback) => {
              setOpenQuickSelectMenu(false); setSelectedSlot(null);
              if (selectedSlot && selectedSlot.callback) {
                selectedSlot.callback();
              }
              if (plannedAction) {
                handlePlannedAction(reload || false, callback)
              }
            }}
            onTaskSelect={(et, replacementSlot) => {
              setOpenQuickSelectMenu(false);
              if (selectedSlot?.selectTaskTypeActionReplacement) {
                selectedSlot.selectTaskTypeActionReplacement(et);
              } else {
                handleOpenScheduleModal(et, false, replacementSlot);
              }
            }}
            onCalendarEventSelect={(et) => { setOpenQuickSelectMenu(false); handleOpenScheduleModal(et, true); }}
            selectedSlot={selectedSlotForMenu}
          />
        </Grid>
      </Grid>
      <Drawer
        anchor="right"
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box
          sx={{
            width: 320,
            maxWidth: '90vw',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              Filters
            </Typography>
            <Button onClick={() => setIsFilterDrawerOpen(false)}>
              Close
            </Button>
          </Box>
          <FilterBar {...filterBarProps} />
        </Box>
      </Drawer>
    </Box>
  );
};

PlannerComponent.propTypes = {
  calendarEvents: PropTypes.bool,
  horseTasks: PropTypes.bool,
  barnTasks: PropTypes.bool,
  defaultView: PropTypes.string,
  defaultAssignment: PropTypes.object,
  onPerformedAction: PropTypes.func,
  navigate: PropTypes.func.isRequired,
  defaultBarnHorses: PropTypes.array,
  defaultCalendarView: PropTypes.string,
};

PlannerComponent.defaultProps = {
  calendarEvents: true,
  horseTasks: true,
  barnTasks: true,
  defaultView: 'grid',
  defaultAssignment: null,
  onPerformedAction: () => { },
  defaultBarnHorses: null,
  defaultCalendarView: null,
};

export default (PlannerComponent);
